import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, Check, Clock, Receipt, Upload, Wallet, X,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

/**
 * Public reimbursement claim — no login.
 *
 * Written for someone standing in a car park who has just bought napkins and
 * wants their money back with the minimum of ceremony. One column, big targets,
 * and the receipt photo captured straight from the camera.
 *
 * The tracking view lives here too: the reference is the credential, and it
 * shows only what the claimant themselves may see.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────── submit ───────────────────────────

export function ClaimPage() {
  const { shareToken } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    claimant_name: "", claimant_email: "", amount: "", category: "Supplies",
    description: "", spent_on: "", receipt_path: "", no_receipt_reason: "",
  });

  useEffect(() => {
    axios.get(`${API}/public/claim/${shareToken}`)
      .then((res) => setInfo(res.data))
      .catch(() => setError("This link is not valid, or the event has been removed."))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await axios.post(`${API}/public/claim/${shareToken}/receipt`, body);
      setForm((f) => ({ ...f, receipt_path: res.data.path, no_receipt_reason: "" }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not upload that file.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!form.claimant_name.trim()) return setError("Please enter your name.");
    if (!form.claimant_email.includes("@")) return setError("Please enter a valid email address.");
    if (!form.amount.trim()) return setError("Please enter the amount you spent.");
    if (!form.description.trim()) return setError("Please say what the money was spent on.");
    if (!form.receipt_path && !form.no_receipt_reason.trim()) {
      return setError("Attach a receipt, or say why there isn't one.");
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/claim/${shareToken}`, form);
      setDone(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto pt-10 h-72 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm" data-testid="claim-invalid">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" aria-hidden="true" />
          <p className="font-medium text-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto pt-10 space-y-4 text-center" data-testid="claim-submitted">
          <div className="h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <Check className="h-7 w-7 text-emerald-600" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {done.duplicate_of ? "You've already claimed this" : "Claim submitted"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {done.duplicate_of
                ? "We found a matching claim, so nothing was submitted twice."
                : `$${done.amount} — we'll email you when it's reviewed.`}
            </p>
          </div>
          <Card>
            <CardContent className="p-5 space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Your reference
              </p>
              <p className="text-lg font-mono font-bold text-foreground break-all"
                 data-testid="claim-reference">{done.reference}</p>
            </CardContent>
          </Card>
          <a href={`/claim/status/${done.reference}`} className="inline-block">
            <Button variant="outline" className="h-11">Track this claim</Button>
          </a>
        </div>
      </div>
    );
  }

  if (!info.claims_open) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-2 max-w-sm" data-testid="claim-closed">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground" aria-hidden="true" />
          <p className="font-medium text-foreground">Claims are closed</p>
          <p className="text-sm text-muted-foreground">
            Please contact the organisers if you're still owed something.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto pt-8 pb-16 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Claim expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">{info.event_title}</p>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cname">Your name</Label>
              <Input id="cname" className="h-11" value={form.claimant_name}
                     onChange={(e) => setForm({ ...form, claimant_name: e.target.value })}
                     data-testid="claim-name" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cemail">Email</Label>
              <Input id="cemail" type="email" className="h-11" value={form.claimant_email}
                     onChange={(e) => setForm({ ...form, claimant_email: e.target.value })}
                     data-testid="claim-email" />
              <p className="text-xs text-muted-foreground">
                We'll email your reference and let you know when it's paid.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="camount">Amount spent</Label>
                <Input id="camount" inputMode="decimal" placeholder="34.20" className="h-11"
                       value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       data-testid="claim-amount" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cdate">Date spent</Label>
                <Input id="cdate" type="date" className="h-11" value={form.spent_on}
                       onChange={(e) => setForm({ ...form, spent_on: e.target.value })}
                       data-testid="claim-date" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>What was it for?</Label>
              <div className="flex flex-wrap gap-2">
                {info.categories.map((cat) => (
                  <Button key={cat} type="button" size="sm" className="h-11"
                          variant={form.category === cat ? "default" : "outline"}
                          onClick={() => setForm({ ...form, category: cat })}
                          data-testid={`claim-cat-${cat}`}>
                    {cat}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cdesc">Description</Label>
              <Textarea id="cdesc" rows={2} placeholder="Napkins and cups from the corner shop"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="claim-description" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="creceipt">Receipt</Label>
              {form.receipt_path ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-500/10 p-3"
                     data-testid="claim-receipt-attached">
                  <span className="text-sm text-emerald-700 dark:text-emerald-500 flex items-center gap-2">
                    <Receipt className="h-4 w-4" aria-hidden="true" />Receipt attached
                  </span>
                  <Button variant="ghost" size="sm" aria-label="Remove receipt"
                          onClick={() => setForm({ ...form, receipt_path: "" })}
                          data-testid="claim-receipt-remove">
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ) : (
                <>
                  {/* capture="environment" opens the camera straight away on a
                      phone, which is where almost all of these come from. */}
                  <Input id="creceipt" type="file" accept="image/*,application/pdf"
                         capture="environment" className="h-11 pt-2"
                         disabled={uploading}
                         onChange={(e) => upload(e.target.files?.[0])}
                         data-testid="claim-receipt" />
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Upload className="h-3 w-3" aria-hidden="true" />
                    {uploading ? "Uploading…" : "Photograph the receipt, or attach a PDF"}
                  </p>
                </>
              )}
            </div>

            {!form.receipt_path && (
              <div className="space-y-1.5">
                <Label htmlFor="noreceipt">No receipt? Tell us why</Label>
                <Input id="noreceipt" className="h-11"
                       placeholder="Lost it, shop didn't give one…"
                       value={form.no_receipt_reason}
                       onChange={(e) => setForm({ ...form, no_receipt_reason: e.target.value })}
                       data-testid="claim-no-receipt" />
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive flex items-start gap-1.5"
                 data-testid="claim-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />{error}
              </p>
            )}

            <Button className="w-full h-12 text-base" onClick={submit}
                    disabled={submitting || uploading} data-testid="claim-submit">
              {submitting ? "Submitting…" : "Submit claim"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────── track ───────────────────────────

const TRACK = {
  submitted: { label: "Waiting to be reviewed", icon: Clock, tone: "text-amber-600" },
  queried: { label: "A question was raised", icon: AlertCircle, tone: "text-violet-600" },
  approved: { label: "Approved — waiting to be paid", icon: Check, tone: "text-blue-600" },
  rejected: { label: "Declined", icon: X, tone: "text-destructive" },
  paid: { label: "Paid", icon: Wallet, tone: "text-emerald-600" },
};

export function ClaimStatusPage() {
  const { reference } = useParams();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/claim-status/${reference}`)
      .then((res) => setClaim(res.data))
      .catch(() => setClaim(null))
      .finally(() => setLoading(false));
  }, [reference]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-sm mx-auto pt-12 h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-2" data-testid="claim-not-found">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" aria-hidden="true" />
          <p className="font-medium text-foreground">Claim not found</p>
          <p className="text-sm text-muted-foreground">Check the reference from your email.</p>
        </div>
      </div>
    );
  }

  const state = TRACK[claim.status] || TRACK.submitted;
  const Icon = state.icon;
  const reduced = claim.approved_amount && claim.approved_amount !== claim.amount;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-sm mx-auto pt-12 space-y-4 text-center">
        <Icon className={`h-12 w-12 mx-auto ${state.tone}`} aria-hidden="true" />
        <div>
          <h1 className="text-xl font-bold text-foreground" data-testid="claim-status">
            {state.label}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{claim.event_title}</p>
        </div>
        <Card>
          <CardContent className="p-5 space-y-1">
            <p className="text-3xl font-bold text-foreground" data-testid="claim-amount-shown">
              ${claim.approved_amount || claim.amount}
            </p>
            {reduced && (
              <p className="text-xs text-muted-foreground" data-testid="claim-reduced">
                Adjusted from ${claim.amount}
              </p>
            )}
            <p className="text-sm text-muted-foreground pt-1">{claim.description}</p>
          </CardContent>
        </Card>
        {claim.note && (
          <p className="text-sm bg-muted rounded-lg p-3 text-muted-foreground"
             data-testid="claim-note">{claim.note}</p>
        )}
        <p className="text-xs text-muted-foreground font-mono break-all">{claim.reference}</p>
      </div>
    </div>
  );
}

export default ClaimPage;
